import angular from 'angular';

import r2a from '@web/angularIntegration/r2a';
import { AddTaskTemplateAngular } from '@web/shiftTasks/AddTaskTemplate/AddTaskTemplateAngular';
import { EditTaskTemplateAngular } from '@web/shiftTasks/EditTaskTemplate/EditTaskTemplateAngular';
import { EditTaskInstanceAngular } from '@web/shiftTasks/EditTaskInstance/EditTaskInstanceAngular';

import { accessLevels } from '../constants';

import _ShiftTasksService from './services/ShiftTasksService';
import _TaskTemplatesService from './services/TaskTemplatesService';
import _ShiftTasksTabsService from './services/ShiftTasksTabsService';
import _ShiftTasksActionsService from './services/ShiftTasksActionsService';
import { ShiftTasksReactBridge as ShiftTasksReactBridgeService } from './services/ShiftTasksReactBridge';
import taskTemplatesComponent from './components/taskTemplates/taskTemplatesComponent';
import taskTemplatesHeaderButtonsComponent from './components/taskTemplates/taskTemplatesHeaderButtonsComponent';
import viewTaskTemplateComponent from './components/modals/viewTaskTemplateComponent';
import viewTaskInstanceComponent from './components/modals/viewTaskInstanceComponent';
import editTaskInstanceComponent from './components/modals/editTaskInstanceComponent';
import subtasksFieldComponent from './components/subtasks/subtasksFieldComponent';
import subtaskAddButtonComponent from './components/subtasks/subtaskAddButtonComponent';
import subtaskFieldComponent from './components/subtasks/subtaskFieldComponent';
import viewSubtasksComponent from './components/subtasks/viewSubtasksComponent';
import viewSubtaskComponent from './components/subtasks/viewSubtaskComponent';
import taskTemplatesListComponent from './components/taskTemplates/templatesList/taskTemplatesListComponent';
import taskTemplateListItemComponent from './components/taskTemplates/templatesList/taskTemplateListItemComponent';
import taskTemplateListItemOptionsComponent from './components/taskTemplates/templatesList/taskTemplateListItemOptionsComponent';
import taskTemplateLookupItemComponent from './components/taskTemplates/taskTemplateLookupItemComponent';
import taskTemplatesPickerFieldComponent from './components/taskTemplates/taskTemplatesPickerFieldComponent';
import taskInstancesViewRowComponent from './components/taskInstances/taskInstancesViewRowComponent';
import taskInstancesComponent from './components/taskInstances/taskInstancesComponent';
import taskInstancesHeaderComponent from './components/taskInstances/taskInstancesHeaderComponent';
import taskInstancesFiltersComponent from './components/taskInstances/taskInstancesFiltersComponent';
import taskInstancesListComponent from './components/taskInstances/taskInstancesList/taskInstancesListComponent';
import taskInstancesHeaderButtonsComponent from './components/taskInstances/taskInstancesHeaderButtonsComponent';
import taskInstanceListItemComponent from './components/taskInstances/taskInstancesList/taskInstanceListItemComponent';
import taskInstanceListItemOptionsComponent from './components/taskInstances/taskInstancesList/taskInstanceListItemOptionsComponent';
import emptyTaskTemplatesComponent from './components/taskTemplates/templatesList/emptyTaskTemplatesComponent';
import emptyTaskInstancesComponent from './components/taskInstances/taskInstancesList/emptyTaskInstancesComponent';
import shiftTasksPreviewComponent from './components/shiftTasksPreviewComponent';
import ShiftTasksFlowCtrl from './ShiftTasksFlowCtrl';
import shiftTasksEntryTemplateUrl from './index.html';
import { SHIFT_TASKS_TAB_NAME, TASK_TEMPLATES_TAB_NAME } from './constants';

const shiftTasksModule = angular.module('sling.shiftTasks', []);
export default shiftTasksModule.name;

shiftTasksModule.config(($stateProvider) => {
  $stateProvider.state('shiftTasks', {
    url: '/shiftTasks',
    abstract: true,
    templateUrl: shiftTasksEntryTemplateUrl,
    controller: ShiftTasksFlowCtrl,
    controllerAs: 'ctrl',
    data: {
      access: accessLevels.user,
    },
    resolve: {
      shiftUtils(ShiftUtils) {
        return ShiftUtils.loadAsync();
      },
      personaCollection(PersonaCollection) {
        return PersonaCollection.loadData();
      },
    },
    onEnter(RateService, ShiftTasksService, $state) {
      if (!ShiftTasksService.isFeatureTurnedOn) {
        $state.go('dashboard.home');
      } else {
        RateService.triggerRateModal();
      }
    },
  });

  $stateProvider.state('shiftTasks.instances', {
    url: '/tasks?date',
    component: 'taskInstances',
    data: {
      title: 'Shift tasks',
      access: accessLevels.user,
    },
    onEnter(ShiftTasksTabsService) {
      ShiftTasksTabsService.setTabByValue(SHIFT_TASKS_TAB_NAME);
    },
  });

  $stateProvider.state('shiftTasks.templates', {
    url: '/templates',
    component: 'taskTemplates',
    data: {
      title: 'Task templates',
      access: accessLevels.manager,
    },
    onEnter(ShiftTasksTabsService) {
      'ngInject';

      ShiftTasksTabsService.setTabByValue(TASK_TEMPLATES_TAB_NAME);
    },
  });

  $stateProvider.state('shiftTasks.templates.add', {
    params: {
      template: null,
    },
    component: 'rAddTaskTemplate',
    resolve: {
      sourceTemplate($stateParams) {
        'ngInject';

        if ($stateParams.template) {
          // eslint-disable-next-line no-unused-vars
          const { id, ...template } = $stateParams.template;
          return template;
        }

        return null;
      },
      onClose($state) {
        'ngInject';

        return () => $state.go('shiftTasks.templates');
      },
      isOpen() {
        return true;
      },
      shiftTasksReactBridge(ShiftTasksReactBridge) {
        'ngInject';

        return ShiftTasksReactBridge;
      },
    },
  });

  function TaskTemplateViewOrEditStateCtrl($scope, $uibModalInstance) {
    'ngInject';

    $scope.modal = $uibModalInstance;
    $scope.templateId = $scope.$resolve.selectedTemplateId;
  }

  $stateProvider.state('shiftTasks.templates.edit', {
    params: {
      templateId: null,
    },
    component: 'rEditTaskTemplate',
    resolve: {
      async originalTemplate(TaskTemplatesService, $stateParams, $state) {
        'ngInject';

        const id = $stateParams.templateId;
        const originalTemplate = await TaskTemplatesService.loadTemplateDetails(id);

        if (!originalTemplate) {
          $state.go('shiftTasks.templates');
        }

        return originalTemplate;
      },
      onClose($state) {
        'ngInject';

        return () => $state.go('shiftTasks.templates');
      },
      isOpen() {
        return true;
      },
      shiftTasksReactBridge(ShiftTasksReactBridge) {
        'ngInject';

        return ShiftTasksReactBridge;
      },
    },
  });

  $stateProvider.state('shiftTasks.templates.view', {
    params: {
      templateId: null,
    },
    resolve: {
      templateId($stateParams) {
        'ngInject';

        return $stateParams.templateId;
      },
    },
    onEnter($uibModal, $state, templateId) {
      'ngInject';

      $uibModal
        .open({
          template:
            '<view-task-template modal="modal" template-id="templateId"></view-task-template>',
          size: '620',
          controller: TaskTemplateViewOrEditStateCtrl,
          resolve: {
            selectedTemplateId: () => Number(templateId),
          },
        })
        .result.catch(() => $state.go('shiftTasks.templates'));
    },
  });

  function TaskInstanceViewOrEditStateCtrl($scope, $uibModalInstance) {
    'ngInject';

    $scope.modal = $uibModalInstance;
    $scope.taskInstanceId = $scope.$resolve.selectedTaskInstanceId;
  }

  $stateProvider.state('shiftTasks.instances.view', {
    url: '/view/{taskInstanceId:int}',
    resolve: {
      taskInstanceId($stateParams) {
        'ngInject';

        return $stateParams.taskInstanceId;
      },
    },
    onEnter($uibModal, $state, taskInstanceId) {
      'ngInject';

      $uibModal
        .open({
          template:
            '<view-task-instance modal="modal" task-instance-id="taskInstanceId"></view-task-instance>',
          size: '620',
          controller: TaskInstanceViewOrEditStateCtrl,
          resolve: {
            selectedTaskInstanceId: () => Number(taskInstanceId),
          },
        })
        .result.catch(() => $state.go('shiftTasks.instances'));
    },
  });

  $stateProvider.state('shiftTasks.instances.edit', {
    url: '/edit/{taskInstanceId:int}',
    params: {
      isFromPreview: false,
    },
    component: 'rEditTaskInstance',
    resolve: {
      async originalInstance(ShiftTasksService, $stateParams, $state) {
        'ngInject';

        const id = $stateParams.taskInstanceId;
        const originalInstance = await ShiftTasksService.loadTaskInstanceDetails(id);

        if (!originalInstance) {
          $state.go('shiftTasks.templates');
        }

        return originalInstance;
      },
      onClose($state) {
        'ngInject';

        return () => $state.go('shiftTasks.instances');
      },
      isOpen() {
        return true;
      },
      shiftTasksReactBridge(ShiftTasksReactBridge) {
        'ngInject';

        return ShiftTasksReactBridge;
      },
      personasDict(PersonaCollection) {
        'ngInject';

        return PersonaCollection.personasDict;
      },
      usTimeFormat(Authentication) {
        'ngInject';

        return Authentication.user.settings.usTimeFormat;
      },
    },
  });
});

shiftTasksModule.service('ShiftTasksService', _ShiftTasksService);
shiftTasksModule.service('ShiftTasksTabsService', _ShiftTasksTabsService);
shiftTasksModule.service('TaskTemplatesService', _TaskTemplatesService);
shiftTasksModule.service('ShiftTasksActionsService', _ShiftTasksActionsService);
shiftTasksModule.service('ShiftTasksReactBridge', ShiftTasksReactBridgeService);

shiftTasksModule.component('taskTemplates', taskTemplatesComponent);
shiftTasksModule.component('taskTemplatesHeaderButtons', taskTemplatesHeaderButtonsComponent);
shiftTasksModule.component('viewTaskTemplate', viewTaskTemplateComponent);
shiftTasksModule.component('viewTaskInstance', viewTaskInstanceComponent);
shiftTasksModule.component('editTaskInstance', editTaskInstanceComponent);
shiftTasksModule.component('subtasksField', subtasksFieldComponent);
shiftTasksModule.component('subtaskAddButton', subtaskAddButtonComponent);
shiftTasksModule.component('subtaskField', subtaskFieldComponent);
shiftTasksModule.component('viewSubtasks', viewSubtasksComponent);
shiftTasksModule.component('viewSubtask', viewSubtaskComponent);
shiftTasksModule.component('taskTemplatesList', taskTemplatesListComponent);
shiftTasksModule.component('taskTemplateListItem', taskTemplateListItemComponent);
shiftTasksModule.component('taskTemplateListItemOptions', taskTemplateListItemOptionsComponent);
shiftTasksModule.component('taskTemplateLookupItem', taskTemplateLookupItemComponent);
shiftTasksModule.component('taskTemplatesPickerField', taskTemplatesPickerFieldComponent);
shiftTasksModule.component('taskInstancesViewRow', taskInstancesViewRowComponent);
shiftTasksModule.component('taskInstances', taskInstancesComponent);
shiftTasksModule.component('taskInstancesHeader', taskInstancesHeaderComponent);
shiftTasksModule.component('taskInstancesFilters', taskInstancesFiltersComponent);
shiftTasksModule.component('taskInstancesList', taskInstancesListComponent);
shiftTasksModule.component('taskInstancesHeaderButtons', taskInstancesHeaderButtonsComponent);
shiftTasksModule.component('taskInstanceListItem', taskInstanceListItemComponent);
shiftTasksModule.component('taskInstanceListItemOptions', taskInstanceListItemOptionsComponent);
shiftTasksModule.component('shiftTasksPreview', shiftTasksPreviewComponent);
shiftTasksModule.component('emptyTaskTemplates', emptyTaskTemplatesComponent);
shiftTasksModule.component('emptyTaskInstances', emptyTaskInstancesComponent);
shiftTasksModule.component(
  'rAddTaskTemplate',
  r2a(AddTaskTemplateAngular, [
    'isOpen',
    'onSubmitSuccess',
    'onClose',
    'shiftTasksReactBridge',
    'sourceTemplate',
  ])
);
shiftTasksModule.component(
  'rEditTaskTemplate',
  r2a(EditTaskTemplateAngular, ['isOpen', 'onClose', 'shiftTasksReactBridge', 'originalTemplate'])
);
shiftTasksModule.component(
  'rEditTaskInstance',
  r2a(EditTaskInstanceAngular, [
    'isOpen',
    'onClose',
    'shiftTasksReactBridge',
    'originalInstance',
    'personasDict',
    'usTimeFormat',
  ])
);
